import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BookPlus, Loader2, Save } from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/contexts/ToastContext";
import { getApiErrorMessage } from "@/lib/api-error";
import { binderyApi, type BinderyVendor } from "@/lib/api/bindery";
import { BinderyItemLineEditor, type DraftItem } from "./new-request/BinderyItemLineEditor";

export default function NewBinderyRequestPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const [vendors, setVendors] = useState<BinderyVendor[]>([]);
  const [vendorId, setVendorId] = useState("");
  const [notes, setNotes] = useState("");
  const [barcode, setBarcode] = useState("");
  const [lookingUp, setLookingUp] = useState(false);
  const [items, setItems] = useState<DraftItem[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    binderyApi.vendors
      .list({ isActive: true, limit: 200, sortBy: "name", sortOrder: "asc" })
      .then((res) => setVendors(res.vendors))
      .catch(() => toast.error("Failed to load vendors"));
  }, [toast]);

  const addItemByBarcode = async () => {
    const code = barcode.trim();
    if (!code) return;
    if (items.some((i) => i.barcode === code)) {
      toast.error("Item already added");
      return;
    }
    setLookingUp(true);
    try {
      const { item, openBindery } = await binderyApi.items.lookup(code);
      if (openBindery) {
        toast.error(`Already in request ${openBindery.request.requestNumber}`);
        return;
      }
      setItems((prev) => [
        ...prev,
        {
          itemId: item.id,
          barcode: item.barcode,
          title: item.biblio.title,
        },
      ]);
      setBarcode("");
    } catch (err) {
      toast.error(getApiErrorMessage(err, ""));
    } finally {
      setLookingUp(false);
    }
  };

  const submit = async (andSend: boolean) => {
    if (!vendorId) return toast.error("Select a vendor");
    if (items.length === 0) return toast.error("Add at least one item");
    setSaving(true);
    try {
      const created = await binderyApi.requests.create({
        vendorId,
        notes: notes.trim() || undefined,
        items: items.map((i) => ({
          itemId: i.itemId,
          damageType: i.damageType,
          repairType: i.repairType,
          instructions: i.instructions?.trim() || undefined,
          cost: i.cost,
        })),
      });
      if (andSend) {
        await binderyApi.requests.send(created.id, {});
        toast.success(`Request ${created.requestNumber} created and sent`);
      } else {
        toast.success(`Draft ${created.requestNumber} saved`);
      }
      navigate(`/bindery/requests/${created.id}`);
    } catch (err) {
      toast.error(getApiErrorMessage(err, ""));
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageContainer width="wide">
      <PageHeader
        title="New bindery request"
        description="Flag damaged items and prepare a batch to send to a vendor."
        breadcrumbItems={[
          { label: "Bindery requests", href: "/bindery/requests" },
          { label: "New request" },
        ]}
        showSearch={false}
        titleSize="md"
        actions={
          <div className="flex gap-2">
            <Button variant="outline" disabled={saving} onClick={() => submit(false)}>
              {saving ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-2 h-4 w-4" />
              )}
              Save draft
            </Button>
            <Button disabled={saving} onClick={() => submit(true)}>
              Save & send
            </Button>
          </div>
        }
      />

      <Card>
        <CardContent className="space-y-4 pt-6">
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label required>Vendor</Label>
              <Select value={vendorId} onValueChange={setVendorId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select vendor" />
                </SelectTrigger>
                <SelectContent>
                  {vendors.map((v) => (
                    <SelectItem key={v.id} value={v.id}>
                      {v.name} — {v.defaultTurnaroundDays}d
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notes</Label>
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional batch notes"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-4 pt-6">
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Label>Add item by barcode</Label>
              <Input
                value={barcode}
                onChange={(e) => setBarcode(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addItemByBarcode();
                  }
                }}
                placeholder="Scan or type a barcode and press Enter"
              />
            </div>
            <Button type="button" onClick={addItemByBarcode} disabled={lookingUp}>
              {lookingUp ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <BookPlus className="mr-2 h-4 w-4" />
              )}
              Add
            </Button>
          </div>

          {items.length === 0 ? (
            <div className="rounded-lg border border-dashed border-slate-200 py-10 text-center text-sm text-slate-500">
              Scan a barcode above to add items to this batch.
            </div>
          ) : (
            <div className="space-y-3">
              {items.map((it, idx) => (
                <BinderyItemLineEditor
                  key={it.itemId}
                  item={it}
                  onChange={(next) =>
                    setItems((prev) => prev.map((p, i) => (i === idx ? next : p)))
                  }
                  onRemove={() => setItems((prev) => prev.filter((_, i) => i !== idx))}
                />
              ))}
              <p className="text-right text-sm font-medium text-slate-600">
                {items.length} item{items.length !== 1 ? "s" : ""} in this batch
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </PageContainer>
  );
}
